import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Alert, Button, Input, Modal, Spin, Table, Tooltip, message } from "antd";
import { deleteSprPred, fetchSprPredList } from "./spr-pred-api";
import SprPred from "./spr-pred";
import { formatDate, normalizeListRow, pick, toNumber } from "./spr-pred-utils";
import "./spr-preds.css";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 350;
const SCROLL_LOAD_GAP = 80;

const getLawFlag = (law, index) => {
  if (!law || law.length <= index) return 1;
  const n = Number(law[index]);
  return Number.isFinite(n) ? n : 1;
};

const normalizePage = (data) => {
  const items = Array.isArray(data)
    ? data
    : Array.isArray(pick(data, "items", "Items"))
      ? pick(data, "items", "Items")
      : [];
  const hasMore = Array.isArray(data)
    ? items.length >= PAGE_SIZE
    : Boolean(pick(data, "hasMore", "HasMore"));
  return {
    items: items.map(normalizeListRow).filter((r) => r.idPred != null),
    hasMore
  };
};

const qFromSearchParams = (params) => {
  const src =
    params instanceof URLSearchParams ? params : new URLSearchParams(params || "");
  return src.has("q") ? src.get("q") ?? "" : "";
};

/**
 * Список контрагентов. Delphi: TFrmSprPred.
 * Подгрузка порциями при скролле; фильтр на бэке.
 */
const SprPreds = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = routeId === "new";
  const editId = routeId && /^\d+$/.test(routeId) ? Number(routeId) : null;
  const drawerOpen = isNew || editId != null;

  const urlQ = useMemo(() => qFromSearchParams(searchParams), [searchParams]);

  const law = useSelector((state) => state.user.session?.law);
  const canEdit = getLawFlag(law, 12) === 1;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [nameFilter, setNameFilter] = useState(() => urlQ);
  const [debouncedQ, setDebouncedQ] = useState(() => (urlQ || "").trim());
  const [hasMore, setHasMore] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const requestIdRef = useRef(0);
  const skipRef = useRef(0);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const listBodyRef = useRef(null);
  const skipUrlSyncRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(nameFilter.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [nameFilter]);

  useEffect(() => {
    const q = debouncedQ;
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (searchParams.toString() === next.toString()) return;
    skipUrlSyncRef.current = true;
    setSearchParams(next, { replace: true });
  }, [debouncedQ, searchParams, setSearchParams]);

  useEffect(() => {
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }
    const fromUrl = qFromSearchParams(searchParams);
    setNameFilter((prev) => (prev === fromUrl ? prev : fromUrl));
    setDebouncedQ((prev) => {
      const q = (fromUrl || "").trim();
      return prev === q ? prev : q;
    });
  }, [searchParams]);

  const loadPage = useCallback(
    ({ reset }) => {
      if (!reset && (loadingMoreRef.current || !hasMoreRef.current)) return;

      const requestId = ++requestIdRef.current;
      const skip = reset ? 0 : skipRef.current;

      if (reset) {
        setLoading(true);
        setError("");
      } else {
        loadingMoreRef.current = true;
        setLoadingMore(true);
      }

      fetchSprPredList({
        q: debouncedQ || undefined,
        skip,
        take: PAGE_SIZE
      })
        .then((data) => {
          if (requestId !== requestIdRef.current) return;
          const page = normalizePage(data);
          setRows((prev) => (reset ? page.items : [...prev, ...page.items]));
          skipRef.current = skip + page.items.length;
          hasMoreRef.current = page.hasMore;
          setHasMore(page.hasMore);
        })
        .catch((e) => {
          if (requestId !== requestIdRef.current) return;
          if (reset) {
            setError(e.message || "Не удалось загрузить контрагентов");
            setRows([]);
            skipRef.current = 0;
            hasMoreRef.current = false;
            setHasMore(false);
          } else {
            message.error(e.message || "Не удалось подгрузить контрагентов");
          }
        })
        .finally(() => {
          if (requestId !== requestIdRef.current) return;
          if (reset) setLoading(false);
          loadingMoreRef.current = false;
          setLoadingMore(false);
        });
    },
    [debouncedQ]
  );

  useEffect(() => {
    if (!canEdit) return;
    skipRef.current = 0;
    hasMoreRef.current = false;
    loadPage({ reset: true });
  }, [canEdit, debouncedQ, loadPage]);

  useEffect(() => {
    if (editId != null) setSelectedId(editId);
  }, [editId]);

  const onListScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      if (!el || loadingMoreRef.current || !hasMoreRef.current) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_LOAD_GAP) {
        loadPage({ reset: false });
      }
    },
    [loadPage]
  );

  const reload = useCallback(() => {
    skipRef.current = 0;
    hasMoreRef.current = false;
    if (listBodyRef.current) listBodyRef.current.scrollTop = 0;
    loadPage({ reset: true });
  }, [loadPage]);

  const selectedRow = useMemo(
    () => rows.find((r) => r.idPred === selectedId) || null,
    [rows, selectedId]
  );

  const openEdit = useCallback(
    (record) => {
      if (record?.idPred == null) return;
      setSelectedId(record.idPred);
      navigate(`/spr/pred/${record.idPred}`);
    },
    [navigate]
  );

  const openCreate = useCallback(() => {
    if (!canEdit) return;
    navigate("/spr/pred/new");
  }, [canEdit, navigate]);

  const closeDrawer = useCallback(() => {
    navigate("/spr/pred");
  }, [navigate]);

  const onSaved = useCallback(
    (saved) => {
      reload();
      const newId = toNumber(saved?.idPred ?? saved?.IdPred);
      if (isNew && newId != null) {
        navigate(`/spr/pred/${newId}`, { replace: true });
      }
    },
    [isNew, reload, navigate]
  );

  const onDelete = useCallback(() => {
    if (!canEdit || selectedRow?.idPred == null) return;
    const id = selectedRow.idPred;

    Modal.confirm({
      title: "Удалить контрагента?",
      content: selectedRow.name || `№${id}`,
      okText: "Удалить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onOk: () =>
        deleteSprPred(id)
          .then(() => {
            message.success("Удалено");
            if (editId === id) navigate("/spr/pred");
            if (selectedId === id) setSelectedId(null);
            reload();
          })
          .catch((e) => {
            message.error(e.message || "Не удалось удалить");
            return Promise.reject(e);
          })
    });
  }, [canEdit, selectedRow, editId, selectedId, reload, navigate]);

  const columns = useMemo(
    () => [
      { title: "#", dataIndex: "idPred", key: "idPred", width: 70 },
      {
        title: "Вид",
        dataIndex: "vidName",
        key: "vidName",
        width: 140,
        ellipsis: true
      },
      {
        title: "Наименование",
        dataIndex: "name",
        key: "name",
        ellipsis: true
      },
      {
        title: "Дата рожд.",
        dataIndex: "dateBird",
        key: "dateBird",
        width: 110,
        render: formatDate
      },
      {
        title: "Телефон",
        dataIndex: "phone",
        key: "phone",
        width: 140,
        ellipsis: true
      },
      {
        title: "Город",
        dataIndex: "city",
        key: "city",
        width: 120,
        ellipsis: true
      },
      {
        title: "Эл. почта",
        dataIndex: "email",
        key: "email",
        width: 160,
        ellipsis: true
      },
      {
        title: "СМС",
        dataIndex: "smsAgree",
        key: "smsAgree",
        width: 60,
        align: "center",
        render: (v) => (v ? <CheckOutlined /> : null)
      }
    ],
    []
  );

  if (!canEdit) {
    return (
      <div className="spr-preds">
        <Alert type="warning" showIcon message="Нет права на справочник контрагентов" />
      </div>
    );
  }

  return (
    <div className="spr-preds">
      {error ? (
        <Alert className="spr-preds__error" type="error" showIcon message={error} />
      ) : null}
      <div className="spr-preds__body">
        <div className="spr-preds__list">
          <div className="spr-preds__toolbar">
            <div className="spr-preds__extra-actions">
              <Tooltip title="Создать">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  aria-label="Создать"
                  onClick={openCreate}
                />
              </Tooltip>
              <Tooltip title={selectedRow ? "Изменить" : "Выберите запись"}>
                <span>
                  <Button
                    icon={<EditOutlined />}
                    disabled={!selectedRow}
                    aria-label="Изменить"
                    onClick={() => selectedRow && openEdit(selectedRow)}
                  />
                </span>
              </Tooltip>
              <Tooltip title={selectedRow ? "Удалить" : "Выберите запись"}>
                <span>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={!selectedRow}
                    aria-label="Удалить"
                    onClick={onDelete}
                  />
                </span>
              </Tooltip>
            </div>
            <Input
              allowClear
              variant="borderless"
              className="spr-preds__filter"
              placeholder="Поиск: имя, телефон, город, почта"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
          </div>
          <div ref={listBodyRef} className="spr-preds__list-body" onScroll={onListScroll}>
            {loading && !rows.length ? (
              <div className="spr-preds__center">
                <Spin tip="Загрузка..." />
              </div>
            ) : (
              <>
                <Table
                  className="spr-preds__table"
                  size="small"
                  rowKey="idPred"
                  columns={columns}
                  dataSource={rows}
                  loading={loading}
                  pagination={false}
                  onRow={(record) => ({
                    onClick: () => {
                      setSelectedId(record.idPred);
                      openEdit(record);
                    }
                  })}
                  rowClassName={(record) =>
                    [
                      "spr-preds__row",
                      selectedId != null && record.idPred === selectedId
                        ? "spr-preds__row--selected"
                        : "",
                      editId != null && record.idPred === editId ? "spr-preds__row--editing" : ""
                    ]
                      .filter(Boolean)
                      .join(" ")
                  }
                  scroll={{ x: 960 }}
                  locale={{
                    emptyText: debouncedQ
                      ? "Нет контрагентов по фильтру"
                      : "Нет контрагентов"
                  }}
                />
                {loadingMore ? (
                  <div className="spr-preds__more">
                    <Spin size="small" /> Подгрузка...
                  </div>
                ) : null}
                {!loading && !hasMore && rows.length > 0 ? (
                  <div className="spr-preds__more spr-preds__more--end">Все записи загружены</div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
      <SprPred
        open={drawerOpen}
        idPred={editId}
        isNew={isNew}
        onClose={closeDrawer}
        onSaved={onSaved}
      />
    </div>
  );
};

export default SprPreds;
